// TODO BNCH-138534: This file will be deprecated once the procedure run plan feature is fully released
import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {pathToURL} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible, accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {makeReactContext} from 'lib/makeReactContext';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import SetStudyPhaseDropdown from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Breadcrumbs, {CrumbLink} from 'ui-library/Breadcrumbs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {StudyOverviewTabContentsProps} from '../utils/TypedStudyTypes';
import AddProcedureRunButton from '../utils/ui/AddProcedureRunButton';
import AnalyzeMultipleProcedureRunsButton from '../utils/ui/AnalyzeMultipleProcedureRunsButton';
import AnalyzeProcedureRunButton from '../utils/ui/AnalyzeProcedureRunButton';
import LinkToExistingUnitOperationsButton from '../utils/ui/LinkToExistingUnitOperationsButton';
import {
  MultiProcedureRunOverviewQuery,
  MultiProcedureRunOverviewQueryVariables,
} from './__generated/MultiProcedureRunOverviewQuery';
import StudyExecutionHeader, {StudyExecutionHeaderTabsProps} from './StudyExecutionHeader';

export enum ProcedureRunTab {
  OVERVIEW = 'overview',
  DESIGN = 'design',
  METADATA = 'metadata',
}

export interface ProcedureRunStateSummary {
  id: string | null;
  name: string | null;
}

export const StudyOverviewContext = makeReactContext<{
  selectedProcedureRun: ProcedureRunStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
}>(
  () => ({
    selectedProcedureRun: {id: null, name: null},
    currentProcedureRunTab: ProcedureRunTab.OVERVIEW,
    setCurrentProcedureRunTab: noop,
  }),
  'StudyOverviewContext'
);

/*
 * Exported for testing
 */
export function studyOverviewCrumbs(props: {
  selectedProcedureRun: ProcedureRunStateSummary;
  studyName: string;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  studyId: string;
}): JSX.Element {
  return (
    <>
      <div {...getTestProps({component: 'MultiProcedureRunOverviewTabContents', element: 'breadcrumbs'})}>
        <Breadcrumbs>
          <CrumbLink
            onClick={() => {
              // resets the procedure run tab to overview when selecting a new procedure run
              props.setCurrentProcedureRunTab(ProcedureRunTab.OVERVIEW);
              navigateToStudyOverviewTab(props.studyId);
            }}
          >
            {props.studyName}
          </CrumbLink>
        </Breadcrumbs>
      </div>
      {props.selectedProcedureRun.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRun.name}</div>
      )}
    </>
  );
}

/*
 * Exported for testing
 */
export function procedureRunViewerTabsProps({
  currentProcedureRunTab,
  setCurrentProcedureRunTab,
  selectedProcedureRunId,
}: {
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  selectedProcedureRunId: string | null;
}): StudyExecutionHeaderTabsProps<ProcedureRunTab> {
  return selectedProcedureRunId != null
    ? {
        headerTabContainerProps: {
          activeTab: currentProcedureRunTab,
          className: css(styles.tabContainer),
          headerDividerClassName: css(styles.headerDivider),
          isFullHeight: true,
          navClassName: css(styles.tabList),
          onRequestTabChange: setCurrentProcedureRunTab,
          paneClassName: css(styles.tabPanel),
          tabClassName: css(styles.tab),
        },
        headerTabsProps: [
          {tabKey: ProcedureRunTab.OVERVIEW, title: 'RUN OVERVIEW'},
          {tabKey: ProcedureRunTab.DESIGN, title: 'DESIGN'},
          {tabKey: ProcedureRunTab.METADATA, title: 'METADATA'},
        ],
      }
    : {};
}

export const MULTI_PROCEDURE_RUN_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $id) {
      ... on ProcedureRunNode {
        id
        name
        archiveData {
          archived
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function MultiProcedureRunOverviewTabContents(
  props: StudyOverviewTabContentsProps
): JSX.Element {
  const procedureRunLabel = getProcedureRunUIStringForStudyType(props.study.studySchema.studyType);
  const copyProcedureRunLinkText = `Copy ${procedureRunLabel} link`;

  const result = useSkippableBenchlingQuery<
    MultiProcedureRunOverviewQuery,
    MultiProcedureRunOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_OVERVIEW_QUERY, {
    variables: {id: props.runId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: props.runId == null,
  });
  let fetchedProcedureRun = null;
  // if a user navigates to the procedure run view via Url, we only show the procedure run view if the run ID
  // is valid and if the user has permission to view the run. Otherwise, we navigate to the procedure run
  // listing view
  if (result.type === QueryResultType.SUCCESS && result.data && !isInaccessible(result.data.procedureRun)) {
    fetchedProcedureRun = result.data.procedureRun;
  }
  const selectedProcedureRun = useMemo(() => {
    if (fetchedProcedureRun != null) {
      return {id: fetchedProcedureRun.id, name: fetchedProcedureRun.name};
    }
    return {id: null, name: null};
  }, [fetchedProcedureRun]);

  // Use useStateIfMounted to because this can get called from the async callback when conditions are added
  // and the user clicks the "open" button in the success toast.
  const [currentProcedureRunTab, setCurrentProcedureRunTab] = useStateIfMounted<ProcedureRunTab>(
    // When the user navigates to a procedure run from the study overview, we want to show the overview tab
    ProcedureRunTab.OVERVIEW
  );

  const studyOverviewContextValue = useMemo(
    () => ({
      selectedProcedureRun,
      currentProcedureRunTab,
      setCurrentProcedureRunTab,
    }),
    [currentProcedureRunTab, selectedProcedureRun, setCurrentProcedureRunTab]
  );

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <StudyOverviewContext.Provider value={studyOverviewContextValue}>
      <StudyExecutionHeader
        {...props}
        dataTestAttrs={{component: 'MultiProcedureRunOverviewTabContents', element: 'overview'}}
        executionContainerStyling={executionContainer}
        headerSectionContainerStyling={headerSectionContainerStyling}
        title={selectedProcedureRun.name ?? undefined}
        headerBreadcrumb={studyOverviewCrumbs({
          selectedProcedureRun,
          studyName: props.study.name,
          setCurrentProcedureRunTab,
          studyId: props.study.id,
        })}
        procedureRunId={selectedProcedureRun.id ?? undefined}
        headerButtons={{
          buttons:
            selectedProcedureRun.id != null ? (
              <>
                <LinkToExistingUnitOperationsButton
                  studyId={props.study.id}
                  procedureRunId={selectedProcedureRun.id}
                  procedureRunName={selectedProcedureRun.name}
                />
                <AnalyzeProcedureRunButton
                  studyId={props.study.id}
                  studyName={props.study.name}
                  studyHumanId={props.study.humanId}
                  isStudyArchived={props.study.archiveRecord != null}
                  studyType={props.study.studySchema.studyType}
                  procedureRunId={selectedProcedureRun.id}
                  procedureRunName={selectedProcedureRun.name}
                  isProcedureRunArchived={fetchedProcedureRun?.archiveData?.archived ?? false}
                  procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                  actionOrigin={ActionOrigin.STUDY_RUN_DETAILS_PAGE}
                />
                <Tooltip text={copyProcedureRunLinkText}>
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    size={ButtonSize.MEDIUM}
                    isDropdown={false}
                    onClick={() =>
                      copyToClipboard(pathToURL(`${props.study.editUrl}?runId=${selectedProcedureRun.id}`), {
                        showToastOnSuccess: true,
                        toastText: `Copied ${procedureRunLabel} link.`,
                      })
                    }
                    icon="link"
                    aria-label={copyProcedureRunLinkText}
                  />
                </Tooltip>
                <Dropdown
                  menu={
                    <Menu height={MenuHeight.SMALL} width={MenuWidth.SMALL} dropLeft={true}>
                      <Option
                        onSelect={() => {
                          exportAuditLogs([selectedProcedureRun.id]);
                        }}
                      >
                        <BenchlingIcon icon="audit-trail" />
                        {` ${EXPORT_AUDIT_LOG_DISPLAY_STRING}`}
                      </Option>
                    </Menu>
                  }
                >
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    icon="overflow"
                    size={ButtonSize.MEDIUM}
                    isDropdown={true}
                    hideDropdownCaret={true}
                  />
                </Dropdown>
              </>
            ) : (
              <>
                <AnalyzeMultipleProcedureRunsButton
                  studyId={props.study.id}
                  studyName={props.study.name}
                  studyHumanId={props.study.humanId}
                  studyType={props.study.studySchema.studyType}
                  isStudyArchived={props.study.archiveRecord != null}
                  procedureRunIds={accessibleObjs(props.study.procedureRuns).map(
                    (procedureRun) => procedureRun.id
                  )}
                  procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                  actionOrigin={ActionOrigin.STUDY_OVERVIEW_PAGE}
                />
                <SetStudyPhaseDropdown
                  study={{
                    id: props.study.id,
                    phase: props.study.phase,
                    studyType: props.study.studySchema.studyType,
                  }}
                  refetch={props.refetch}
                  isDisabled={!props.isEditable}
                  requiresApproval={props.isDesignApprovalRequiredToProceed}
                  buttonSize={ButtonSize.MEDIUM}
                  allowReturnToDesignPhase={props.allowReturnToDesignPhase}
                />
                <AddProcedureRunButton
                  studyId={props.study.id}
                  studyType={props.study.studySchema.studyType}
                />
              </>
            ),
          buttonsStyling: buttonStyling,
        }}
        {...procedureRunViewerTabsProps({
          currentProcedureRunTab,
          setCurrentProcedureRunTab,
          selectedProcedureRunId: selectedProcedureRun?.id ?? null,
        })}
      />
    </StudyOverviewContext.Provider>
  );
}

const styles = StyleSheet.create({
  breadcrumbsFont: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
  },
  headerDivider: {
    borderBottom: `1.5px solid ${Colors.GRAY_LIGHT}`,
  },
  tab: {
    ...FONT_PROXIMA_12,
  },
  tabContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    color: Colors.GRAY_DARKER,
    marginTop: 18,
  },
  tabList: {
    marginLeft: '12px',
  },
  tabPanel: {
    marginTop: 0,
  },
});

const executionContainer: React.CSSProperties = {
  flex: '1 0',
};

const buttonStyling: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
};

const headerSectionContainerStyling: React.CSSProperties = {
  margin: '0 12px 12px',
};
